import { useIsMutating } from '@tanstack/react-query';
import React, { useState, useSyncExternalStore } from 'react';
import { Layout, Button, Card, Menu, Tag } from 'antd';
import {
  FileSearchOutlined,
  CloudServerOutlined,
  LogoutOutlined,
  SafetyCertificateOutlined,
  TeamOutlined,
  ShoppingOutlined,
  FundOutlined,
  CrownOutlined,
  AlertOutlined,
} from '@ant-design/icons';
import { useLocation, useNavigate, Outlet } from 'react-router-dom';
import { getApiBaseUrl, isMonitorAvailable, readApiEnvironment } from '../config/env';
import { SessionMenu } from '../auth/SessionMenu';
import { getPendingWrites, subscribePendingWrites } from '../api/client';
import { useAuth } from '../auth/AuthProvider';

const { Header, Content, Sider } = Layout;

const NAV_GROUPS = [
  { key: 'game-settings', label: 'Game settings', children: [
    { key: '/global-config', icon: <SafetyCertificateOutlined />, label: 'Global Config' },
    { key: '/rooms-config', icon: <TeamOutlined />, label: 'Rooms Config' },
    { key: '/bot-fill-config', icon: <TeamOutlined />, label: 'Bot Fill Config' },
    { key: '/vip-configs', icon: <CrownOutlined />, label: 'VIP Config' },
    { key: '/events', icon: <CrownOutlined />, label: 'Events' },
    { key: '/catalog', icon: <ShoppingOutlined />, label: 'Shop & IAP' },
    { key: '/geo-access/rules', icon: <SafetyCertificateOutlined />, label: 'Geo Block' },
    { key: '/game-servers', icon: <CloudServerOutlined />, label: 'Game Servers' },
  ] },
  { key: 'observability', label: 'Observability', children: [
    { key: '/monitor', icon: <FundOutlined />, label: 'System Monitor' },
    { key: '/system-alert', icon: <AlertOutlined />, label: 'System Alert' },
    { key: '/metrics', icon: <FundOutlined />, label: 'Game Metrics' },
  ] },
  { key: 'finance', label: 'Finance', children: [
    { key: '/transactions', icon: <FileSearchOutlined />, label: 'Wallet Transactions' },
    { key: '/daily-support-history', icon: <FileSearchOutlined />, label: 'Daily Support History' },
    { key: '/rewarded-ads-history', icon: <FileSearchOutlined />, label: 'Rewarded Ads Claims' },
    { key: '/pay-segments', icon: <FundOutlined />, label: 'Pay Segments' },
  ] },
  { key: 'members', label: 'Members', children: [
    { key: '/users', icon: <TeamOutlined />, label: 'Users' },
    { key: '/administrators', icon: <SafetyCertificateOutlined />, label: 'Administrators' },
  ] },
];

export const AdminLayout: React.FC = () => {
  const { logout } = useAuth();
  const writes = useSyncExternalStore(subscribePendingWrites, getPendingWrites) + useIsMutating();
  const environment = readApiEnvironment();
  const environmentColor = { dev: 'blue', staging: 'gold', 'staging-testpay': 'orange', production: 'red', local: 'green', other: 'purple' }[environment];
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  const activeKey = location.pathname.startsWith('/geo-access') ? '/geo-access/rules'
    : location.pathname.startsWith('/users/') ? '/users'
    : location.pathname.startsWith('/administrators/') ? '/administrators' : location.pathname;
  const activeLabel = activeKey === '/home' ? 'Home' : NAV_GROUPS.flatMap((group) => group.children).find((item) => item.key === activeKey)?.label;

  return (
    <Layout className="min-h-screen bg-[#f5f7fb]">
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        breakpoint="md"
        collapsedWidth={72}
        width={240}
        theme="dark"
        style={{ backgroundColor: '#0f172a', position: 'sticky', top: 0, height: '100dvh', overflowY: 'auto', paddingBottom: 48 }}
      >
        <button
          type="button"
          aria-label="Go to Home"
          onClick={() => navigate('/home')}
          className="h-16 w-full px-6 my-2 flex items-center overflow-hidden text-white font-semibold tracking-tight text-left bg-transparent border-0 cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-400"
          title={`Golden Hounds · ${environment === 'other' ? 'Custom' : environment}`}
        >
          <SafetyCertificateOutlined className="text-xl shrink-0" />

          {!collapsed && (
            <div className="ml-3 flex flex-col items-start gap-1">
              <span className="whitespace-nowrap text-lg">Golden Hounds</span>

              <Tag color={environmentColor} title={getApiBaseUrl()} className="font-bold" style={{ marginInlineEnd: 0 }}>
                {environment === 'other' ? 'CUSTOM' : environment.toUpperCase()}
              </Tag>
            </div>
          )}
        </button>

        <Menu
          className="admin-sidebar-menu"
          theme="dark"
          mode="inline"
          selectedKeys={[activeKey]}
          items={NAV_GROUPS.map((group) => ({ ...group, type: 'group' as const,
            children: group.children.filter((item) => item.key !== '/monitor' || isMonitorAvailable()),
          }))}
          onClick={({ key }) => navigate(key)}
          style={{ backgroundColor: '#0f172a' }}
        />
      </Sider>

      <Layout className="bg-[#f5f7fb] min-w-0">
        <Header
          className="h-16 px-4 md:px-8 flex items-center justify-between bg-white border-b border-slate-200"
          style={{ lineHeight: 'normal' }}
        >
          <span className="font-semibold text-slate-800 text-lg">
            {activeLabel}
          </span>

          <div className="flex items-center gap-3 md:gap-4 min-w-0">
            <SessionMenu />
            <Button
              type="text"
              disabled={writes > 0}
              icon={<LogoutOutlined />}
              onClick={() => logout()}
            >
              Logout
            </Button>
          </div>
        </Header>

        <Content
          className={activeKey === '/game-servers' ? 'admin-content--game-floor' : activeKey === '/monitor' ? 'p-2 md:p-4 w-full' : 'p-4 md:p-8 max-w-7xl mx-auto w-full'}
        >
          {activeKey === '/game-servers' || activeKey === '/monitor' ? <Outlet /> : (
          <Card className="shadow-sm rounded-lg border-slate-200">
            <Outlet />
          </Card>
          )}
        </Content>
      </Layout>
    </Layout>
  );
};
